"use client";

import { createContext, useContext, useEffect, useState } from "react";

import type * as Comlink from "comlink";

/**
 * Shared state type for all database providers.
 * Generic over the worker API type so each app gets type-safe access.
 */
export type DbState<TApi> =
  | { status: "loading" }
  | { status: "ready"; api: Comlink.Remote<TApi>; isInMemoryMode: boolean }
  | { status: "error"; error: string };

export interface DatabaseProviderConfig<TApi> {
  /** The Comlink-wrapped worker API instance. */
  api: Comlink.Remote<TApi>;
  /** Promise that resolves when the worker has finished init(). */
  initPromise: Promise<void>;
  /** Method on the worker API that returns the in-memory mode flag. */
  getIsInMemoryMode: (api: Comlink.Remote<TApi>) => Promise<boolean>;
  /**
   * Optional callback that runs after the database is initialized and ready.
   * Use for post-init tasks like vacation shift (flashcards).
   */
  onReady?: (api: Comlink.Remote<TApi>) => Promise<void>;
}

/**
 * Create a typed DatabaseProvider + hooks for a specific app.
 *
 * Usage:
 * ```ts
 * const { DatabaseProvider, useDatabase, ... } = createDatabaseProvider<WorkerApi>();
 * ```
 */
export function createDatabaseProvider<TApi>() {
  const DatabaseContext = createContext<DbState<TApi>>({ status: "loading" });

  function DatabaseProvider({
    config,
    children,
  }: {
    config: DatabaseProviderConfig<TApi>;
    children: React.ReactNode;
  }) {
    const [state, setState] = useState<DbState<TApi>>({ status: "loading" });

    useEffect(() => {
      let disposed = false;

      void (async () => {
        try {
          await config.initPromise;

          if (config.onReady) {
            await config.onReady(config.api);
          }

          const inMemory = await config.getIsInMemoryMode(config.api);
          if (!disposed) {
            setState({ status: "ready", api: config.api, isInMemoryMode: inMemory });
          }
        } catch (err) {
          if (!disposed) {
            const message = err instanceof Error ? err.message : "Failed to initialize database";
            setState({ status: "error", error: message });
          }
        }
      })();

      return () => {
        disposed = true;
      };
    }, [config]);

    return <DatabaseContext.Provider value={state}>{children}</DatabaseContext.Provider>;
  }

  function useDatabase(): Comlink.Remote<TApi> {
    const ctx = useContext(DatabaseContext);
    if (ctx.status !== "ready") {
      throw new Error("useDatabase must be used within a DatabaseProvider after initialization");
    }
    return ctx.api;
  }

  function useIsInMemoryMode(): boolean {
    const ctx = useContext(DatabaseContext);
    if (ctx.status !== "ready") return false;
    return ctx.isInMemoryMode;
  }

  function useDatabaseStatus(): DbState<TApi> {
    return useContext(DatabaseContext);
  }

  function DatabaseGate({
    children,
    fallback,
    renderError,
  }: {
    children: React.ReactNode;
    fallback?: React.ReactNode;
    renderError?: (error: string) => React.ReactNode;
  }) {
    const state = useDatabaseStatus();
    if (state.status === "loading") return <>{fallback ?? null}</>;
    if (state.status === "error") {
      if (renderError) return <>{renderError(state.error)}</>;
      return <>Database error: {state.error}</>;
    }
    return <>{children}</>;
  }

  return {
    DatabaseProvider,
    useDatabase,
    useIsInMemoryMode,
    useDatabaseStatus,
    DatabaseGate,
  };
}
